import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import React from 'react';
import { v4 as uuid } from 'uuid';
import { IImpersonationContextComponentProps } from '../../../../../models';
import { getDisplayName } from '../../../../../models/NameUtils';
import { useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useUserQuery } from '../../../../../queries';
import { IConnectionTypeComponentProps } from '../../../../containers/templates/EditCampaign/context';
import { Checkbox } from '../../../Checkbox';
import { DeprecatedSelect, ISelectOption } from '../../../DeprecatedSelect';
import {
	SelectAnEmployeeExternalEmailBody,
	SelectAnEmployeeInternalEmailBody,
	SelectAnEmployeeModal,
} from '../../../SelectAnEmployee';
import { styleSheet } from './../styles';

interface Props extends IImpersonationContextComponentProps, IConnectionTypeComponentProps {
	canEdit?: boolean;
	step: Api.AutomationTemplateEditorStep<Api.AutomationStepViewModel<Api.UserSessionContext, Api.IAutomationStep>>;
	sendFromLabel?: string;
	showInternalSendFromOption?: boolean;
	onSave?: (option: Api.ISendFromOptions, sendToSender: boolean) => void;
}
export function StepSendFromOption(props: Props) {
	const userSession = useUserSession();
	const canEdit = props.canEdit !== false;
	const hasSendOptionSelected = () => {
		return (
			!!props.step.automationStep?.sendFromOptions &&
			props.step.automationStep.sendFromOptions.mode !== Api.SendEmailFrom.CurrentUser
		);
	};
	const [selectAnEmployeeModalIsOpen, setSelectAnEmployeeModalIsOpen] = React.useState(false);
	const [sendFrom, setSendFrom] = React.useState(props.step.automationStep?.sendFromOptions);
	const [sendToSender, setSendToSender] = React.useState(
		props.step.automationStep?.type === Api.AutomationStepType.Email
			? !!(props.step.automationStep as Api.EmailAutomationStepViewModel).sendToSender
			: false
	);
	const [showingSendFrom, setShowingSendFrom] = React.useState(() => {
		return hasSendOptionSelected() && !sendToSender;
	});

	const [selectedSendFromUser, setSelectedSendFromUser] = React.useState<Api.IUser | null>();
	const onSaveSelectAnEmployee = (user: Api.IUser) => {
		if (!canEdit) {
			return;
		}
		setSelectAnEmployeeModalIsOpen(false);
		setSelectedSendFromUser(user);
		const updatedOption = { mode: Api.SendEmailFrom.SelectedUser, selectedUser: user.id };
		setSendFrom(updatedOption);
		props.onSave(updatedOption, sendToSender);
	};
	const connectionOptions = props.connectionTypes?.length
		? props.connectionTypes.map(connectionType => {
				return {
					id: `connectionType-${connectionType.value}`,
					dataContext: {
						mode: Api.SendEmailFrom.ConnectionType,
						connectionType: connectionType.value,
					} as Api.ISendFromOptions,
					text: connectionType.label,
				};
			})
		: [];
	const sendFromOptions = [
		{
			id: Api.SendEmailFrom.ContactOwner,
			dataContext: { mode: Api.SendEmailFrom.ContactOwner },
			text: 'Contact Owner',
		},
		...connectionOptions,
		{
			id: Api.SendEmailFrom.SelectedUser,
			dataContext: { mode: Api.SendEmailFrom.SelectedUser },
			text: 'Selected User',
		},
	];
	const onSendFromSelectionChanged = (option: ISelectOption<Api.ISendFromOptions>, wasSelected: boolean) => {
		if (!canEdit) {
			return;
		}
		if (wasSelected) {
			if (option.dataContext.mode === Api.SendEmailFrom.SelectedUser) {
				setSelectAnEmployeeModalIsOpen(true);
				return;
			}
			setSelectedSendFromUser(null);
			setSendFrom(option.dataContext);
			props.onSave(option.dataContext, sendToSender);
		}
	};
	const onCloseSelectAnEmployeeModal = () => setSelectAnEmployeeModalIsOpen(false);
	const selectedUserQuery = useUserQuery({
		enabled: Boolean(props.step.automationStep?.sendFromOptions?.selectedUser),
		id: props.step.automationStep?.sendFromOptions?.selectedUser ?? '',
		impersonationContext: props.impersonationContext,
	});
	const onShowSendFromCheckboxChanged = () => {
		if (!canEdit) {
			return;
		}
		if (showingSendFrom) {
			setSendFrom(null);
			setShowingSendFrom(false);
			setSelectedSendFromUser(null);
			props.onSave(null, sendToSender);
		} else {
			const newSendFrom = sendFromOptions[0].dataContext;
			const newSendToSender = false;
			setSendFrom(newSendFrom);
			setShowingSendFrom(true);
			setSendToSender(newSendToSender);
			setSelectedSendFromUser(null);
			props.onSave(newSendFrom, newSendToSender);
		}
	};
	const getSendFromOptionLabel = (option: Api.ISendFromOptions) => {
		const allOptions = sendFromOptions;
		if (!allOptions) {
			return null;
		}

		if (option.mode === Api.SendEmailFrom.SelectedUser) {
			if (selectedSendFromUser) {
				return getDisplayName(selectedSendFromUser);
			}

			return selectedUserQuery.data ? getDisplayName(selectedUserQuery.data) : 'Selected User';
		}
		return allOptions.find(
			i => i.dataContext.mode === option.mode && i.dataContext.connectionType === option.connectionType
		)?.text;
	};
	const onSendToSenderCheckboxChanged = () => {
		if (!canEdit) {
			return;
		}
		if (sendToSender) {
			const newSendToSender = false;
			setSendFrom(null);
			setSendToSender(newSendToSender);
			setSelectedSendFromUser(null);
			props.onSave(null, newSendToSender);
		} else {
			const newSendFrom = sendFromOptions[0].dataContext;
			const newSendToSender = true;
			setSendFrom(newSendFrom);
			setSendToSender(newSendToSender);
			setShowingSendFrom(false);
			setSelectedSendFromUser(null);
			props.onSave(newSendFrom, newSendToSender);
		}
	};

	return (
		<>
			<Checkbox
				className={css(styleSheet.addTagOnLinkClickCheckbox)}
				id={props.step.uuid ? `automation-send-from-checkbox-${props.step.uuid}` : uuid()}
				checked={showingSendFrom}
				disabled={props.step.automationStep.isBusy || !canEdit}
				type='large'
				onChange={onShowSendFromCheckboxChanged}
			>
				<div>
					<p className={css(styleSheet.checkboxText)}>{props?.sendFromLabel ?? 'Always send this step from'}</p>
				</div>
			</Checkbox>
			{showingSendFrom ? (
				<DeprecatedSelect
					maxOptionsToShow={99}
					onOptionClick={onSendFromSelectionChanged}
					options={sendFromOptions}
					selectedOptionTitle={getSendFromOptionLabel(sendFrom)}
				/>
			) : null}
			{props?.showInternalSendFromOption && userSession.account.features.automation.showInternalSendOption ? (
				<>
					<Checkbox
						className={css(styleSheet.addTagOnLinkClickCheckbox)}
						id={props.step.uuid ? `automation-send-to-checkbox-${props.step.uuid}` : uuid()}
						checked={sendToSender}
						disabled={props.step.automationStep.isBusy || !canEdit}
						type='large'
						onChange={onSendToSenderCheckboxChanged}
					>
						<div>
							<p className={css(styleSheet.checkboxText)}>Send this email to an internal user</p>
						</div>
					</Checkbox>
					{sendToSender ? (
						<DeprecatedSelect
							maxOptionsToShow={99}
							onOptionClick={onSendFromSelectionChanged}
							options={sendFromOptions}
							selectedOptionTitle={getSendFromOptionLabel(sendFrom)}
						/>
					) : null}
				</>
			) : null}
			{selectAnEmployeeModalIsOpen ? (
				<SelectAnEmployeeModal
					onClose={onCloseSelectAnEmployeeModal}
					onUserSelected={onSaveSelectAnEmployee}
					bodyText={sendToSender ? SelectAnEmployeeInternalEmailBody : SelectAnEmployeeExternalEmailBody}
				/>
			) : null}
		</>
	);
}
